/**
 * ExplosiveFart - Timed explosive with warning and massive blast
 * 
 * Behavior:
 * 1. Places a "bomb" that pulses with warning animation
 * 2. After 0.8s delay, explodes with massive radius
 * 3. CAN damage the owner (high risk, high reward)
 * 4. Pierces through walls (shockwave effect)
 * 5. Applies knockback to all hit players
 */

import Phaser from 'phaser';
import { FartType, FART_CONFIGS, EXPLOSIVE_CONFIG } from './FartTypes';
import { IFart } from '../../interfaces/GameInterfaces';

export interface ExplosiveFartConfig {
    ownerId: string;
}

enum ExplosiveState {
    CHARGING = 'charging',
    EXPLODING = 'exploding',
    DONE = 'done'
}

export class ExplosiveFart extends Phaser.Physics.Arcade.Sprite implements IFart {
    public readonly fartType: FartType = FartType.EXPLOSIVE;

    private ownerId: string;
    private explosiveState: ExplosiveState = ExplosiveState.CHARGING;
    private spawnTime: number;
    private hasExploded: boolean = false;
    private hitPlayers: Set<string> = new Set();

    // Visual elements
    private warningCircle?: Phaser.GameObjects.Graphics;
    private blastCircle?: Phaser.GameObjects.Graphics;

    // Config values
    private readonly CHARGE_DELAY = EXPLOSIVE_CONFIG.chargeDelay;
    private readonly BLAST_RADIUS = EXPLOSIVE_CONFIG.blastRadius;
    private readonly KNOCKBACK_FORCE = EXPLOSIVE_CONFIG.knockbackForce;
    private readonly DAMAGE = FART_CONFIGS[FartType.EXPLOSIVE].damage;
    private readonly STUN_DURATION = FART_CONFIGS[FartType.EXPLOSIVE].stunDuration;

    constructor(scene: Phaser.Scene, x: number, y: number, config: ExplosiveFartConfig) {
        super(scene, x, y, 'fart_explosive');

        this.ownerId = config.ownerId;
        this.spawnTime = scene.time.now;

        // Add to scene and physics
        scene.add.existing(this);
        scene.physics.add.existing(this);

        // Configure physics body for blast detection
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setCircle(16);
        body.setOffset(8, 8);
        body.setImmovable(true);

        // Visual setup
        this.setDepth(45);
        this.setScale(0.8);

        // Store owner data
        this.setData('ownerId', this.ownerId);
        this.setData('isExplosive', true);

        // Create warning circle
        this.createWarningEffect();

        // Schedule explosion
        scene.time.delayedCall(this.CHARGE_DELAY, () => {
            this.explode();
        });
    }

    /**
     * Create the pulsing warning effect before explosion
     */
    private createWarningEffect(): void {
        this.warningCircle = this.scene.add.graphics();
        this.warningCircle.setDepth(44);

        // Pulsing warning animation
        const pulseWarning = () => {
            if (!this.active || this.explosiveState !== ExplosiveState.CHARGING) return;

            this.warningCircle?.clear();

            // Growing danger ring
            const progress = (this.scene.time.now - this.spawnTime) / this.CHARGE_DELAY;
            const currentRadius = this.BLAST_RADIUS * progress;

            this.warningCircle?.lineStyle(3, 0xff0000, 0.5 + 0.5 * Math.sin(progress * Math.PI * 6));
            this.warningCircle?.strokeCircle(this.x, this.y, currentRadius);

            // Inner fast pulse
            const pulseScale = 0.8 + 0.4 * Math.sin(this.scene.time.now * 0.02);
            this.setScale(pulseScale);
            this.setTint(Phaser.Display.Color.GetColor(255, 100 + 155 * (1 - progress), 100 + 155 * (1 - progress)));
        };

        // Update warning every frame during charge
        this.scene.events.on('update', pulseWarning);

        // Clean up listener when done
        this.scene.time.delayedCall(this.CHARGE_DELAY, () => {
            this.scene.events.off('update', pulseWarning);
        });
    }

    /**
     * Trigger the explosion
     */
    private explode(): void {
        if (this.hasExploded || !this.active) return;

        this.hasExploded = true;
        this.explosiveState = ExplosiveState.EXPLODING;

        // Clear warning circle
        this.warningCircle?.destroy();

        // Update physics body to blast radius
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setCircle(this.BLAST_RADIUS);
        body.setOffset(-this.BLAST_RADIUS + 16, -this.BLAST_RADIUS + 16);

        // Create explosion visual
        this.createExplosionEffect();

        // Camera shake for everyone
        this.scene.cameras.main.shake(200, 0.02);

        // Schedule cleanup
        this.scene.time.delayedCall(100, () => {
            this.explosiveState = ExplosiveState.DONE;
        });

        this.scene.time.delayedCall(500, () => {
            this.destroy();
        });
    }

    /**
     * Create the explosion visual effect
     */
    private createExplosionEffect(): void {
        this.blastCircle = this.scene.add.graphics();
        this.blastCircle.setDepth(48);

        // Instant blast wave
        const colors = [0xff4444, 0xff8800, 0xffcc00];

        // Multiple expanding rings
        for (let i = 0; i < 3; i++) {
            const ring = this.scene.add.graphics();
            ring.setDepth(47 - i);

            this.scene.tweens.add({
                targets: { radius: 0, alpha: 1 },
                radius: this.BLAST_RADIUS * (1 + i * 0.2),
                alpha: 0,
                duration: 300 + i * 100,
                ease: 'Power2',
                onUpdate: (tween) => {
                    const val = tween.targets[0] as { radius: number; alpha: number };
                    ring.clear();
                    ring.lineStyle(8 - i * 2, colors[i], val.alpha);
                    ring.strokeCircle(this.x, this.y, val.radius);
                    ring.fillStyle(colors[i], val.alpha * 0.3);
                    ring.fillCircle(this.x, this.y, val.radius * 0.5);
                },
                onComplete: () => ring.destroy()
            });
        }

        // Flash the sprite
        this.setScale(3);
        this.setAlpha(1);
        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            scaleX: 4,
            scaleY: 4,
            duration: 300,
            ease: 'Power2'
        });
    }

    /**
     * Check if explosion is active (for damage detection)
     */
    isExploding(): boolean {
        return this.explosiveState === ExplosiveState.EXPLODING;
    }

    /**
     * Check if a player was already hit by this explosion
     */
    wasPlayerHit(playerId: string): boolean {
        return this.hitPlayers.has(playerId);
    }

    /**
     * Mark player as hit
     */
    markPlayerHit(playerId: string): void {
        this.hitPlayers.add(playerId);
    }

    /**
     * CAN damage owner (explosive can self-damage)
     */
    canDamagePlayer(playerId: string): boolean {
        // Already hit this player
        if (this.hitPlayers.has(playerId)) return false;

        // Explosive CAN damage owner - that's the risk!
        return true;
    }

    /**
     * Get knockback force for a player
     */
    getKnockbackForce(playerX: number, playerY: number): { x: number; y: number } {
        const angle = Phaser.Math.Angle.Between(this.x, this.y, playerX, playerY);
        return {
            x: Math.cos(angle) * this.KNOCKBACK_FORCE,
            y: Math.sin(angle) * this.KNOCKBACK_FORCE
        };
    }

    /**
     * Get the owner ID
     */
    getOwnerId(): string {
        return this.ownerId;
    }

    /**
     * Calculate damage based on distance from explosion center
     * Closer = more damage, edge of blast = less damage
     */
    getDamageAtDistance(playerX: number, playerY: number): number {
        const dist = Phaser.Math.Distance.Between(this.x, this.y, playerX, playerY);

        // Full damage at center, 30% damage at edge
        const falloffStart = this.BLAST_RADIUS * 0.3; // Full damage within 30% of radius
        const falloffEnd = this.BLAST_RADIUS;

        if (dist <= falloffStart) {
            return this.DAMAGE; // Full damage
        }

        // Linear falloff from 100% to 30%
        const falloffProgress = (dist - falloffStart) / (falloffEnd - falloffStart);
        const damageMultiplier = 1 - (falloffProgress * 0.7); // 1.0 to 0.3

        return Math.round(this.DAMAGE * Math.max(0.3, damageMultiplier));
    }

    /**
     * IFart interface - get base damage value
     */
    getDamage(): number {
        return this.DAMAGE;
    }

    /**
     * IFart interface - get stun duration
     */
    getStunDuration(): number {
        return this.STUN_DURATION;
    }

    /**
     * Override destroy to clean up
     */
    destroy(fromScene?: boolean): void {
        this.warningCircle?.destroy();
        this.blastCircle?.destroy();
        super.destroy(fromScene);
    }
}
